import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Avatar,
    CircularProgress,
    TablePagination
} from '@mui/material';
import { getSubteamMembers } from 'team/apis/teamSubteamApi';
import avatarPlaceholder from 'images/avatar-cropped.png';

const TeamSubteamMembersViewContainer = ({ subteamId }) => {
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalMembers, setTotalMembers] = useState(0);

    useEffect(() => {
        const fetchMembers = async () => {
            if (!subteamId) return;

            setLoading(true);
            try {
                const response = await getSubteamMembers(subteamId, {
                    page: page + 1,
                    limit: rowsPerPage,
                    status: 'active'
                });

                if (response && response.success) {
                    setMembers(response.members || []);
                    setTotalMembers(response.pagination?.totalMembers || 0);
                } else if (response && response.members) {
                    // Handle case where response structure is different
                    setMembers(Array.isArray(response.members) ? response.members : []);
                    setTotalMembers(response.pagination?.totalMembers || response.members?.length || 0);
                } else if (Array.isArray(response)) {
                    setMembers(response);
                    setTotalMembers(response.length);
                }
            } catch (error) {
                console.error('[TeamSubteamMembersViewContainer] Error fetching members:', error);
                setMembers([]);
            } finally {
                setLoading(false);
            }
        };

        fetchMembers();
    }, [subteamId, page, rowsPerPage]);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const getMemberName = (member) => {
        return member.user?.fullName || member.fullName || 'Unknown';
    };

    const getMemberEmail = (member) => {
        return member.user?.email || member.email || '';
    };

    const getMemberAvatar = (member) => {
        return member.user?.profilePictureUrl || member.profilePictureUrl || avatarPlaceholder;
    };

    const getMemberRole = (member) => {
        return member.role === 'admin' ? 'Admin' : 'Member';
    };

    return (
        <Box sx={{ padding: 3 }}>
            <Typography variant="h6" sx={{ marginBottom: 3, fontWeight: 600 }}>
                Subteam Members
            </Typography>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
                    <CircularProgress />
                </Box>
            ) : members.length === 0 ? (
                <Box sx={{ padding: 4, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                        No members found in this subteam.
                    </Typography>
                </Box>
            ) : (
                <>
                    <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #e0e0e0' }}>
                        <Table>
                            <TableHead>
                                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                    <TableCell sx={{ fontWeight: 600 }}>Member</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Role</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {members.map((member) => (
                                    <TableRow key={member._id || member.userId} hover>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                <Avatar
                                                    src={getMemberAvatar(member)}
                                                    alt={getMemberName(member)}
                                                    sx={{ width: 40, height: 40 }}
                                                />
                                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                    {getMemberName(member)}
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" color="text.secondary">
                                                {getMemberEmail(member)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">
                                                {getMemberRole(member)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    color: member.status === 'active' ? '#4caf50' : '#828282',
                                                    fontWeight: member.status === 'active' ? 500 : 400
                                                }}
                                            >
                                                {member.status === 'active' ? 'Active' : member.status || 'Active'}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {totalMembers > rowsPerPage && (
                        <TablePagination
                            component="div"
                            count={totalMembers}
                            page={page}
                            onPageChange={handleChangePage}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            rowsPerPageOptions={[10, 25, 50]}
                        />
                    )}
                </>
            )}
        </Box>
    );
};

export default TeamSubteamMembersViewContainer;
